import React, { useEffect } from 'react';
import { Alert, Button, Card, Result, Space, Tag, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { store } from '../redux/store';
import { setLoading } from '../redux/slice/loadingSlice';

const { Paragraph, Text } = Typography;

type ResultContentConfig = {
  title: string;
  message: string;
  primaryActionLabel?: string;
  primaryActionUrl?: string;
  helpTitle?: string;
  possibleReasons?: string[];
  nextSteps?: string[];
  supportMessage?: string;
};

const RESULT_CONTENT_MAP: Record<string, ResultContentConfig> = {
  SUBSCRIPTION_INACTIVE: {
    title: 'Abonelik erişimi durduruldu',
    message:
      'Aboneliğiniz şu anda aktif görünmüyor. Bu durum başarısız ödeme, iptal edilen paket veya tamamlanmamış abonelik adımlarından kaynaklanabilir.',
    primaryActionLabel: 'Aboneliği yönet',
    primaryActionUrl: '/subscription/upgrade',
    helpTitle: 'Bu durum ne anlama geliyor?',
    possibleReasons: [
      'Ödeme işlemi tamamlanmamış olabilir.',
      'Abonelik süresi sona ermiş olabilir.',
      'Paket iptal edilmiş veya askıya alınmış olabilir.',
      'Abonelik doğrulama süreci tamamlanamamış olabilir.',
    ],
    nextSteps: [
      'Abonelik ve ödeme durumunuzu kontrol edin.',
      'Gerekirse ödeme adımını yeniden başlatın.',
      'Sorun devam ederse destek ekibiyle iletişime geçin.',
    ],
    supportMessage:
      'Yetkili kullanıcıysanız aboneliği yeniden başlatabilirsiniz. Standart kullanıcıysanız şirket yöneticinizle iletişime geçmeniz gerekir.',
  },
};

const SubscriptionResultPage: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    store.dispatch(setLoading(false));
  }, []);

  const searchParams = new URLSearchParams(location.search);

  const code = searchParams.get('code') ?? '';
  const ctaLabel = searchParams.get('ctaLabel') ?? '';
  const ctaUrl = searchParams.get('ctaUrl') ?? '';
  const status = searchParams.get('status') ?? '';

  const fallbackTitle = searchParams.get('title') ?? 'Bilgilendirme';
  const fallbackMessage =
    searchParams.get('message') ??
    'İşleminizle ilgili bir bilgilendirme bulunmaktadır.';

  const config = RESULT_CONTENT_MAP[code] ?? {
    title: fallbackTitle,
    message: fallbackMessage,
    primaryActionLabel: ctaLabel || undefined,
    primaryActionUrl: ctaUrl || undefined,
  };

  const isSubscriptionInactive = code === 'SUBSCRIPTION_INACTIVE';

  const handlePrimaryAction = () => {
    if (!config.primaryActionUrl) return;
    navigate(config.primaryActionUrl);
  };

  const handleLogout = () => {
    localStorage.clear();
    logout();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#f5f5f5',
      }}
    >
      <Card
        style={{ width: '100%', maxWidth: 760, borderRadius: 16 }}
        styles={{ body: { padding: 32 } }}
      >
        <Result
          status={isSubscriptionInactive ? 'warning' : 'info'}
          title={config.title}
          subTitle={config.message}
          extra={
            <Space wrap>
              {config.primaryActionLabel && config.primaryActionUrl ? (
                <Button type="primary" onClick={handlePrimaryAction}>
                  {config.primaryActionLabel}
                </Button>
              ) : null}

              <Button danger onClick={handleLogout}>
                Çıkış yap
              </Button>
            </Space>
          }
        />

        {isSubscriptionInactive ? (
          <div style={{ marginTop: 8 }}>
            <Alert
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
              message={config.helpTitle}
              description="Abonelik durumunuz doğrulanamadığında ücretli modüllere erişim geçici olarak durdurulur. Aşağıdaki adımlar sorunu hızlıca çözmenize yardımcı olur."
              style={{ marginBottom: 24, borderRadius: 12 }}
            />

            <div
              style={{
                display: 'grid',
                gap: 16,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              }}
            >
              <Card size="small" title="Olası nedenler" style={{ borderRadius: 12 }}>
                <Space direction="vertical" size={8}>
                  {config.possibleReasons?.map((reason) => (
                    <Text key={reason}>{`• ${reason}`}</Text>
                  ))}
                </Space>
              </Card>

              <Card size="small" title="Ne yapabilirsiniz?" style={{ borderRadius: 12 }}>
                <Space direction="vertical" size={8}>
                  {config.nextSteps?.map((step) => (
                    <Text key={step}>{`• ${step}`}</Text>
                  ))}
                </Space>
              </Card>
            </div>

            <Card
              size="small"
              title="Yetki ve yönlendirme"
              style={{ marginTop: 16, borderRadius: 12 }}
            >
              <Paragraph style={{ marginBottom: 0 }}>
                {config.supportMessage}
              </Paragraph>
            </Card>

            {status ? (
              <div style={{ marginTop: 16 }}>
                <Tag color="gold">Durum kodu: {status}</Tag>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default SubscriptionResultPage;